import { apiClient } from '@api'

/**
 * Task Service
 *
 * Handles all task management API calls for checklist workflow operations
 * Tasks are checklist items viewed from the perspective of assigned users
 */

const taskService = {
	/**
	 * Get all tasks (Admin only - for All Tasks tab)
	 * @param {Object} params - Query parameters (project_id, workflow_status, assigned_to, search, page, limit)
	 * @returns {Promise} Tasks list with pagination
	 */
	async getAll(params = {}) {
		const queryString = new URLSearchParams(params).toString()
		const endpoint = queryString ? `/tasks?${queryString}` : '/tasks'
		return apiClient.get(endpoint)
	},

	/**
	 * Get tasks assigned to current user
	 * @param {Object} params - Query parameters (project_id, workflow_status, search, page, limit)
	 * @returns {Promise} User's tasks with pagination
	 */
	async getMyTasks(params = {}) {
		const queryString = new URLSearchParams(params).toString()
		const endpoint = queryString ? `/tasks/my-tasks?${queryString}` : '/tasks/my-tasks'
		return apiClient.get(endpoint)
	},

	/**
	 * Get tasks pending approval by current user's role
	 * @param {Object} params - Query parameters (project_id, search, page, limit)
	 * @returns {Promise} Approval queue with pagination
	 */
	async getMyApprovals(params = {}) {
		const queryString = new URLSearchParams(params).toString()
		const endpoint = queryString
			? `/tasks/my-approvals?${queryString}`
			: '/tasks/my-approvals'
		return apiClient.get(endpoint)
	},

	/**
	 * Get task statistics for current user
	 * @returns {Promise} Task counts by status (pending, in_progress, pending_review, completed, rejected)
	 */
	async getStatistics() {
		return apiClient.get('/tasks/statistics')
	},

	/**
	 * Get single task details with workflow information
	 * @param {number} id - Task ID (checklist ID)
	 * @returns {Promise} Task details with workflow steps and history
	 */
	async getById(id) {
		return apiClient.get(`/tasks/${id}`)
	},

	/**
	 * Start working on a task (change status to in_progress)
	 * Valid from: pending, rejected
	 * @param {number} id - Task ID
	 * @returns {Promise} Result
	 */
	async start(id) {
		return apiClient.post(`/tasks/${id}/start`)
	},

	/**
	 * Submit task for review (advance to next workflow step)
	 * Valid from: in_progress
	 * @param {number} id - Task ID
	 * @param {Object} data - Submission data
	 * @param {string} data.notes - Completion notes (optional)
	 * @param {boolean} data.evidence_attached - Whether evidence was attached (optional)
	 * @returns {Promise} Result
	 */
	async submit(id, data = {}) {
		return apiClient.post(`/tasks/${id}/submit`, data)
	},

	/**
	 * Accept task (approver accepts - changes status to pending_review)
	 * After accepting, user can then submit to next step or complete if last step
	 * @param {number} id - Task ID
	 * @returns {Promise} Result with is_last_step flag
	 */
	async accept(id) {
		return apiClient.post(`/tasks/${id}/accept`)
	},

	/**
	 * Complete task (final step - marks workflow as 100% complete)
	 * Only available at the last workflow step after accepting
	 * @param {number} id - Task ID
	 * @param {Object} data - Completion data
	 * @param {string} data.notes - Completion notes (optional)
	 * @returns {Promise} Result
	 */
	async complete(id, data = {}) {
		return apiClient.post(`/tasks/${id}/complete`, data)
	},

	/**
	 * Approve task (alias for accept - backward compatibility)
	 * @deprecated Use accept() instead
	 * @param {number} id - Task ID
	 * @param {Object} data - Approval data (ignored)
	 * @returns {Promise} Result
	 */
	async approve(id, data = {}) {
		return apiClient.post(`/tasks/${id}/approve`, data)
	},

	/**
	 * Reject task and return to previous assignee
	 * User must have rejection rights for current workflow step
	 * @param {number} id - Task ID
	 * @param {Object} data - Rejection data
	 * @param {string} data.remarks - Required rejection remarks
	 * @returns {Promise} Result
	 */
	async reject(id, data) {
		if (!data || !data.remarks) {
			throw new Error('Rejection remarks are required')
		}
		return apiClient.post(`/tasks/${id}/reject`, data)
	},

	/**
	 * Manually assign task to a user (for pending_assignment tasks)
	 * Only Company Admin, Compliance Manager, and Project Manager can assign
	 * @param {number} id - Task ID
	 * @param {Object} data - Assignment data
	 * @param {number} data.user_id - User ID to assign task to
	 * @returns {Promise} Result
	 */
	async assignManually(id, data) {
		if (!data || !data.user_id) {
			throw new Error('User ID is required for task assignment')
		}
		return apiClient.post(`/tasks/${id}/assign`, data)
	},

	/**
	 * Helper: Check if user can start a task
	 * Valid from: not_started, pending_assignment, rejected
	 *
	 * @param {Object} task - Task object
	 * @param {Object} user - Current user object (required for assignment check)
	 * @returns {boolean} True if task can be started
	 */
	canStart(task, user = null) {
		if (!task) return false
		// Valid start statuses: not_started, pending_assignment, rejected, or empty (initial state)
		const validStartStatuses = ['not_started', 'pending_assignment', 'rejected', '']
		if (!validStartStatuses.includes(task.workflow_status)) {
			return false
		}
		// User must be provided and assigned to the task
		if (!user || !task.assigned_to) {
			return false
		}
		return Number(task.assigned_to) === Number(user.id)
	},

	/**
	 * Helper: Check if user can submit a task for review
	 * Valid from: in_progress (user is working on task)
	 *
	 * Flow: in_progress → Submit → pending_review
	 *
	 * @param {Object} task - Task object
	 * @param {Object} user - Current user object (required for assignment check)
	 * @returns {boolean} True if task can be submitted
	 */
	canSubmit(task, user = null) {
		if (!task) return false
		// Can only submit when task is in_progress
		if (task.workflow_status !== 'in_progress') {
			return false
		}
		// User must be provided and assigned to the task
		if (!user || !task.assigned_to) {
			return false
		}
		return Number(task.assigned_to) === Number(user.id)
	},

	/**
	 * Helper: Check if user can accept a pending_review task (approver)
	 * Valid from: pending_review (task waiting for approval)
	 *
	 * Flow: pending_review → Accept → in_progress (approver now working)
	 *
	 * @param {Object} task - Task object
	 * @param {string} userRoleSlug - Current user's role slug
	 * @returns {boolean} True if user can accept the task
	 */
	canAccept(task, userRoleSlug) {
		if (!task) return false
		// Can only accept tasks in pending_review status
		if (task.workflow_status !== 'pending_review') {
			return false
		}
		// Must have workflow info with current step
		if (!task.workflow_info || !task.workflow_info.current_step) {
			return false
		}
		// User's role must match the current step's required role
		return task.workflow_info.current_step.role_slug === userRoleSlug
	},

	/**
	 * Helper: Check if user can reject a task (approver)
	 * Valid from: pending_review or in_progress (after accepting)
	 *
	 * @param {Object} task - Task object
	 * @param {string} userRoleSlug - Current user's role slug
	 * @returns {boolean} True if user can reject the task
	 */
	canReject(task, userRoleSlug) {
		if (!task) return false
		// Can reject from pending_review or in_progress (after accepting)
		if (!['pending_review', 'in_progress'].includes(task.workflow_status)) {
			return false
		}
		// Must have workflow info with current step
		if (!task.workflow_info || !task.workflow_info.current_step) {
			return false
		}
		// User's role must match the current step's required role
		return task.workflow_info.current_step.role_slug === userRoleSlug
	},

	/**
	 * Helper: Check if user can accept/reject a task (approver)
	 * Valid from: pending_review (for accept) or in_progress (for reject after accepting)
	 *
	 * @param {Object} task - Task object
	 * @param {string} userRoleSlug - Current user's role slug
	 * @returns {boolean} True if user can accept/reject
	 */
	canAcceptOrReject(task, userRoleSlug) {
		// Return true if user can accept (pending_review)
		return this.canAccept(task, userRoleSlug)
	},

	/**
	 * Helper: Check if user can complete a task (final step)
	 * Valid from: in_progress (last step, after accepting)
	 *
	 * Flow: in_progress (last step) → Complete → approved (100%)
	 *
	 * @param {Object} task - Task object
	 * @param {Object} user - Current user object (required for assignment check)
	 * @returns {boolean} True if user can complete the task
	 */
	canComplete(task, user = null) {
		if (!task) return false
		// Must be in_progress
		if (task.workflow_status !== 'in_progress') {
			return false
		}
		// Must be last step
		if (!this.isLastWorkflowStep(task)) {
			return false
		}
		// User must be provided and assigned to the task
		if (!user || !task.assigned_to) {
			return false
		}
		return Number(task.assigned_to) === Number(user.id)
	},

	/**
	 * @deprecated Use canAcceptOrReject instead
	 */
	canApproveOrReject(task, userRoleSlug) {
		return this.canAcceptOrReject(task, userRoleSlug)
	},

	/**
	 * @deprecated This is no longer needed - after Accept, status becomes in_progress
	 */
	canSubmitAfterAccept(task, user = null) {
		// After accepting, the status is in_progress, so use canSubmit
		return this.canSubmit(task, user)
	},

	/**
	 * Helper: Check if this is the last workflow step
	 * @param {Object} task - Task object with workflow_info
	 * @returns {boolean} True if this is the last step
	 */
	isLastWorkflowStep(task) {
		if (!task || !task.workflow_info) {
			return false
		}
		const { current_step_index, total_steps } = task.workflow_info
		if (total_steps === undefined || total_steps === 0) {
			return false
		}
		// current_step_index is 0-based, so last step is when index === total_steps - 1
		return current_step_index === total_steps - 1
	},

	/**
	 * Helper: Check if this is the first workflow step
	 * @param {Object} task - Task object with workflow_info
	 * @returns {boolean} True if this is the first step
	 */
	isFirstWorkflowStep(task) {
		if (!task || !task.workflow_info) {
			return true // Default to true if no workflow info
		}
		const currentIndex = task.workflow_info.current_step_index
		return currentIndex === 0 || currentIndex === undefined
	},

	/**
	 * Helper: Check if evidence is required before submission
	 * Evidence is MANDATORY at the first workflow step.
	 * Subsequent steps can optionally add more documents.
	 *
	 * @param {Object} task - Task object with workflow_info
	 * @param {number} documentsCount - Number of evidence documents uploaded
	 * @returns {boolean} True if evidence requirement is satisfied
	 */
	isEvidenceRequirementSatisfied(task, documentsCount = 0) {
		// If not at first step, evidence is optional (already uploaded at first step)
		if (!this.isFirstWorkflowStep(task)) {
			return true
		}
		// At first step, must have at least one evidence document
		return documentsCount > 0
	},

	/**
	 * Helper: Check if user can submit, considering evidence requirement
	 * @param {Object} task - Task object
	 * @param {Object} user - Current user object
	 * @param {number} documentsCount - Number of evidence documents uploaded
	 * @returns {Object} { canSubmit: boolean, reason: string|null }
	 */
	canSubmitWithEvidence(task, user, documentsCount = 0) {
		// First check basic submit permissions
		if (!this.canSubmit(task, user)) {
			return { canSubmit: false, reason: 'Cannot submit task in current state' }
		}

		// Check evidence requirement at first step
		if (this.isFirstWorkflowStep(task) && documentsCount === 0) {
			return {
				canSubmit: false,
				reason: 'Evidence document is required before submitting for review',
			}
		}

		return { canSubmit: true, reason: null }
	},

	/**
	 * Helper: Check if user can complete, considering evidence requirement
	 * @param {Object} task - Task object
	 * @param {Object} user - Current user object
	 * @param {number} documentsCount - Number of evidence documents uploaded
	 * @returns {Object} { canComplete: boolean, reason: string|null }
	 */
	canCompleteWithEvidence(task, user, documentsCount = 0) {
		// First check basic complete permissions
		if (!this.canComplete(task, user)) {
			return { canComplete: false, reason: 'Cannot complete task in current state' }
		}

		// For single-step workflows (first = last), evidence is mandatory
		if (this.isFirstWorkflowStep(task) && documentsCount === 0) {
			return {
				canComplete: false,
				reason: 'Evidence document is required before completing the task',
			}
		}

		return { canComplete: true, reason: null }
	},

	/**
	 * Helper: Check if task is locked (approved and non-editable)
	 * @param {Object} task - Task object
	 * @returns {boolean} True if task is locked
	 */
	isLocked(task) {
		return task && (task.is_locked === 1 || task.workflow_status === 'approved')
	},

	/**
	 * Helper: Get workflow progress percentage
	 * @param {Object} task - Task object with workflow_info
	 * @returns {number} Progress percentage (0-100)
	 */
	getWorkflowProgress(task) {
		if (!task || !task.workflow_info) {
			return 0
		}

		const { current_step_index, total_steps } = task.workflow_info

		if (!total_steps || total_steps === 0) {
			return 0
		}

		if (task.workflow_status === 'approved') {
			return 100
		}

		// Calculate progress based on completed steps
		const completedSteps = current_step_index || 0
		return Math.round((completedSteps / total_steps) * 100)
	},

	/**
	 * Helper: Get task status badge color
	 * @param {string} status - Workflow status
	 * @returns {string} Badge color class
	 */
	getStatusColor(status) {
		const statusColors = {
			not_started: 'gray',
			pending_assignment: 'yellow',
			pending: 'blue',
			in_progress: 'purple',
			pending_review: 'orange',
			approved: 'green',
			rejected: 'red',
			not_applicable: 'gray',
		}
		return statusColors[status] || 'gray'
	},

	/**
	 * Helper: Get task status display text
	 * @param {string} status - Workflow status
	 * @returns {string} Display text
	 */
	getStatusText(status) {
		const statusText = {
			not_started: 'Not Started',
			pending_assignment: 'Pending Assignment',
			pending: 'Pending',
			in_progress: 'In Progress',
			pending_review: 'Pending Review',
			approved: 'Completed',
			completed: 'Completed',
			rejected: 'Rejected',
			not_applicable: 'Not Applicable',
		}
		return statusText[status] || status
	},

	// ========================================
	// TASK NOTES API
	// ========================================

	/**
	 * Get all notes for a task
	 * @param {number} taskId - Task/Checklist ID
	 * @param {Object} params - Optional query parameters (type, limit, offset)
	 * @returns {Promise} Notes list
	 */
	async getNotes(taskId, params = {}) {
		const queryString = new URLSearchParams(params).toString()
		const endpoint = queryString
			? `/tasks/${taskId}/notes?${queryString}`
			: `/tasks/${taskId}/notes`
		return apiClient.get(endpoint)
	},

	/**
	 * Add a note to a task
	 * @param {number} taskId - Task/Checklist ID
	 * @param {Object} data - Note data
	 * @param {string} data.note - Note content
	 * @param {string} data.note_type - Note type (comment, status_change, approval, rejection, system)
	 * @returns {Promise} Created note
	 */
	async addNote(taskId, data) {
		if (!data || !data.note || !data.note.trim()) {
			throw new Error('Note content is required')
		}
		return apiClient.post(`/tasks/${taskId}/notes`, data)
	},

	/**
	 * Update a note (owner only)
	 * @param {number} taskId - Task/Checklist ID
	 * @param {number} noteId - Note ID
	 * @param {Object} data - Note data
	 * @param {string} data.note - Updated note content
	 * @returns {Promise} Updated note
	 */
	async updateNote(taskId, noteId, data) {
		if (!data || !data.note || !data.note.trim()) {
			throw new Error('Note content is required')
		}
		return apiClient.put(`/tasks/${taskId}/notes/${noteId}`, data)
	},

	/**
	 * Delete a note (owner only)
	 * @param {number} taskId - Task/Checklist ID
	 * @param {number} noteId - Note ID
	 * @returns {Promise} Result
	 */
	async deleteNote(taskId, noteId) {
		return apiClient.delete(`/tasks/${taskId}/notes/${noteId}`)
	},

	// ========================================
	// TASK DOCUMENTS (EVIDENCE) API
	// ========================================

	/**
	 * Get all documents for a task
	 * @param {number} taskId - Task/Checklist ID
	 * @returns {Promise} Documents list
	 */
	async getDocuments(taskId) {
		return apiClient.get(`/tasks/${taskId}/documents`)
	},

	/**
	 * Upload document to a task
	 * Allowed types: PDF, DOC, DOCX, JPG, PNG, GIF, WebP
	 * Max size: 10MB
	 * @param {number} taskId - Task/Checklist ID
	 * @param {File} file - File to upload
	 * @param {Function} onProgress - Optional progress callback
	 * @returns {Promise} Uploaded document
	 */
	async uploadDocument(taskId, file, onProgress = null) {
		const formData = new FormData()
		formData.append('file', file)

		// Use fetch directly for multipart/form-data with progress
		// Check both storage keys for token (sessionStorage first, then localStorage)
		const token = sessionStorage.getItem('auth_access_token') || localStorage.getItem('auth_access_token')
		const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'

		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest()

			if (onProgress) {
				xhr.upload.addEventListener('progress', (e) => {
					if (e.lengthComputable) {
						const percentComplete = (e.loaded / e.total) * 100
						onProgress(percentComplete)
					}
				})
			}

			xhr.addEventListener('load', () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					try {
						resolve(JSON.parse(xhr.responseText))
					} catch {
						resolve(xhr.responseText)
					}
				} else {
					try {
						reject(JSON.parse(xhr.responseText))
					} catch {
						reject(new Error(`Upload failed: ${xhr.status}`))
					}
				}
			})

			xhr.addEventListener('error', () => {
				reject(new Error('Network error during upload'))
			})

			xhr.open('POST', `${baseUrl}/tasks/${taskId}/documents`)
			if (token) {
				xhr.setRequestHeader('Authorization', `Bearer ${token}`)
			}
			xhr.send(formData)
		})
	},

	/**
	 * Delete a document
	 * @param {number} taskId - Task/Checklist ID
	 * @param {number} documentId - Document ID
	 * @returns {Promise} Result
	 */
	async deleteDocument(taskId, documentId) {
		return apiClient.delete(`/tasks/${taskId}/documents/${documentId}`)
	},

	// ========================================
	// TASK CONFIGURATION API
	// ========================================

	/**
	 * Get detailed task information including counts
	 * @param {number} taskId - Task/Checklist ID
	 * @returns {Promise} Task details with notes_count, documents_count, workflow
	 */
	async getTaskDetails(taskId) {
		return apiClient.get(`/checklists/${taskId}/task-details`)
	},

	/**
	 * Configure/edit task details (CM/PM only)
	 * @param {number} taskId - Task/Checklist ID
	 * @param {Object} data - Configuration data
	 * @param {string} data.title - Task title (optional)
	 * @param {string} data.description - Task description (optional)
	 * @param {string} data.due_date - Due date YYYY-MM-DD (optional)
	 * @param {boolean} data.evidence_required - Whether evidence is required
	 * @returns {Promise} Updated task
	 */
	async configureTask(taskId, data) {
		return apiClient.put(`/checklists/${taskId}/configure-task`, data)
	},

	// ========================================
	// HELPER FUNCTIONS
	// ========================================

	/**
	 * Helper: Validate file for upload
	 * @param {File} file - File to validate
	 * @returns {Object} { valid: boolean, error: string|null }
	 */
	validateEvidenceFile(file) {
		const maxSize = 10 * 1024 * 1024 // 10MB
		const allowedTypes = [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'image/jpeg',
			'image/jpg',
			'image/png',
			'image/gif',
			'image/webp',
		]
		const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'webp']

		if (file.size > maxSize) {
			return { valid: false, error: 'File size exceeds 10MB limit' }
		}

		const extension = file.name.split('.').pop().toLowerCase()
		if (!allowedExtensions.includes(extension)) {
			return {
				valid: false,
				error: `Invalid file type. Allowed: ${allowedExtensions.join(', ').toUpperCase()}`,
			}
		}

		if (!allowedTypes.includes(file.type)) {
			// Some browsers may not correctly identify MIME types, so we rely on extension
			console.warn('MIME type mismatch, but extension is valid:', file.type)
		}

		return { valid: true, error: null }
	},

	/**
	 * Helper: Format file size for display
	 * @param {number} bytes - File size in bytes
	 * @returns {string} Formatted size
	 */
	formatFileSize(bytes) {
		if (bytes >= 1048576) {
			return (bytes / 1048576).toFixed(2) + ' MB'
		} else if (bytes >= 1024) {
			return (bytes / 1024).toFixed(2) + ' KB'
		} else {
			return bytes + ' bytes'
		}
	},

	/**
	 * Helper: Get note type icon/style
	 * @param {string} noteType - Note type
	 * @returns {Object} { icon: string, className: string }
	 */
	getNoteTypeStyle(noteType) {
		const styles = {
			comment: { icon: 'message-circle', className: 'note--comment' },
			status_change: { icon: 'refresh-cw', className: 'note--status' },
			approval: { icon: 'check-circle', className: 'note--approval' },
			rejection: { icon: 'x-circle', className: 'note--rejection' },
			system: { icon: 'info', className: 'note--system' },
		}
		return styles[noteType] || styles.comment
	},
}

export default taskService
